/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/call/CallRecordingCard.tsx
 * PURPOSE: OpenPhone-style call log card — duration, scrubber, playback speed, summary; **`useCallRecordingPlayback`** when **`metadata.recordingUrl`**
 * DEPENDENCIES: react, ../../types/activity.types, ../activity/activityFeedCallDisplay, ./useCallRecordingPlayback
 * PUBLIC_API: CallRecordingCard
 * SIDE_EFFECTS: Delegates audio I/O to hook when URL present
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 182/200
 * RECENT: 2026-05-07 [2.2.858] **`recordingUrl`** + **`useCallRecordingPlayback`** — root **`CHANGELOG` [2.2.858]`**
 */

import { type ReactElement } from 'react';
import type { Activity } from '../../types/activity.types';
import { callOutcomeBadge, callOutcomeTonePillClassName, formatCallDuration } from '../activity/activityFeedCallDisplay';
import { useCallRecordingPlayback } from './useCallRecordingPlayback';

const SPEEDS = [1, 1.5, 2] as const;

function summaryBullets(activity: Activity): string[] {
  const lines = activity.metadata?.transcript;
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.map((s) => String(s).trim()).filter(Boolean);
  }
  const n = activity.notes?.trim();
  if (!n) return [];
  return n.split(/\n+/).map((s) => s.trim()).filter(Boolean);
}

function recordingSource(activity: Activity): string | null {
  const raw = activity.metadata?.recordingUrl?.trim();
  return raw ? raw : null;
}

export type CallRecordingCardProps = {
  activity: Activity;
};

export function CallRecordingCard({ activity }: CallRecordingCardProps): ReactElement {
  const src = recordingSource(activity);
  const {
    audioRef,
    scrubDragRef,
    scrub,
    rate,
    setRate,
    playing,
    syncScrubFromAudio,
    applyScrubPercent,
    togglePlay,
    audioEvents,
  } = useCallRecordingPlayback(src, activity.id);

  const dur = activity.metadata?.durationSeconds;
  const durationLabel =
    typeof dur === 'number' && Number.isFinite(dur) ? formatCallDuration(dur) : '—';
  const outcome = callOutcomeBadge(activity);
  const bullets = summaryBullets(activity);

  return (
    <div
      className="mt-2 rounded-xl border border-gray-200 bg-gray-50/90 p-3 shadow-sm dark:border-gray-600 dark:bg-gray-900/50"
      data-testid={`call-recording-card-${activity.id}`}
    >
      {src ? (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          className="sr-only"
          data-testid={`call-recording-audio-${activity.id}`}
          aria-hidden
          onLoadedMetadata={syncScrubFromAudio}
          onDurationChange={syncScrubFromAudio}
          onTimeUpdate={syncScrubFromAudio}
          {...audioEvents}
        />
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Recording
        </span>
        <span
          className="font-mono text-xs font-medium text-gray-800 dark:text-gray-200"
          data-testid={`call-recording-duration-${activity.id}`}
        >
          {durationLabel}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {outcome ? (
          <span
            className={callOutcomeTonePillClassName(outcome.tone)}
            data-testid={`call-recording-outcome-${activity.id}`}
          >
            {outcome.label}
          </span>
        ) : null}
      </div>
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between gap-2">
          <label
            className="block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
            htmlFor={`call-recording-scrub-${activity.id}`}
          >
            Playback
          </label>
          {src ? (
            <button
              type="button"
              onClick={() => void togglePlay()}
              className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              data-testid={`call-recording-play-${activity.id}`}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
          ) : null}
        </div>
        <input
          id={`call-recording-scrub-${activity.id}`}
          type="range"
          min={0}
          max={100}
          step={0.25}
          value={scrub}
          onPointerDown={() => {
            scrubDragRef.current = true;
          }}
          onPointerUp={() => {
            scrubDragRef.current = false;
            syncScrubFromAudio();
          }}
          onPointerCancel={() => {
            scrubDragRef.current = false;
            syncScrubFromAudio();
          }}
          onChange={(e) => applyScrubPercent(Number(e.target.value))}
          className="h-2 w-full cursor-pointer accent-primary-600 disabled:cursor-not-allowed"
          aria-label={src ? 'Playback position' : 'Playback position (no audio file yet)'}
          title={src ? 'Drag to seek' : 'Recording playback coming soon — scrubber is a preview control'}
          data-testid={`call-recording-scrub-${activity.id}`}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Speed
        </span>
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setRate(s)}
            className={
              rate === s
                ? 'rounded-md bg-primary-600 px-2 py-0.5 text-[11px] font-semibold text-white'
                : 'rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
            }
            data-testid={`call-recording-speed-${s}x-${activity.id}`}
          >
            {s}x
          </button>
        ))}
      </div>
      {bullets.length > 0 ? (
        <div
          className="mt-3 space-y-1.5 text-sm leading-snug text-gray-800 dark:text-gray-200"
          data-testid={`call-recording-summary-${activity.id}`}
        >
          {bullets.map((line, i) => (
            <div key={`${i}-${line.slice(0, 32)}`} className="flex gap-2">
              <span className="shrink-0 text-primary-600 dark:text-primary-400" aria-hidden>
                •
              </span>
              <span className="min-w-0">{line}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs italic text-gray-500 dark:text-gray-400">No summary lines for this call.</p>
      )}
    </div>
  );
}
